import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  useTheme,
  alpha
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { format, startOfWeek, endOfWeek, isSameWeek } from 'date-fns';

const StatCard = ({ icon, title, value, subtitle, color }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{
      p: 2,
      borderRadius: 2,
      background: `linear-gradient(135deg, ${alpha(color, 0.1)}, ${alpha(color, 0.05)})`,
      border: `1px solid ${alpha(color, 0.2)}`,
      transition: 'transform 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)'
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          p: 1,
          borderRadius: 1.5,
          backgroundColor: alpha(color, 0.15),
          color: color
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1.1rem' }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ScheduleStats = ({ employees, tasks, shifts, currentDate }) => {
  // Use tasks if provided, otherwise fall back to shifts for compatibility
  const currentTasks = tasks || shifts;
  const theme = useTheme();

  // Calculate current week stats
  const currentWeekStart = startOfWeek(currentDate);
  const currentWeekEnd = endOfWeek(currentDate);
  
  const currentWeekTasks = currentTasks.filter(task => 
    isSameWeek(new Date(task.shift_date), currentDate)
  );

  // Total tasks this week
  const totalTasks = currentWeekTasks.length;

  // Total scheduled hours this week
  const totalHours = currentWeekTasks.reduce((total, task) => {
    const startTime = new Date(`2000-01-01T${task.start_time}`);
    const endTime = new Date(`2000-01-01T${task.end_time}`);
    const taskHours = (endTime - startTime) / (1000 * 60 * 60);
    return total + taskHours;
  }, 0);

  // Employees with tasks this week
  const employeesWithTasks = new Set(currentWeekTasks.map(task => task.employee_id)).size;

  // Calculate overtime hours (over 40 hours per week per employee)
  const overtimeData = {};
  currentWeekTasks.forEach(task => {
    if (!overtimeData[task.employee_id]) {
      overtimeData[task.employee_id] = 0;
    }
    const startTime = new Date(`2000-01-01T${task.start_time}`);
    const endTime = new Date(`2000-01-01T${task.end_time}`);
    const taskHours = (endTime - startTime) / (1000 * 60 * 60);
    overtimeData[task.employee_id] += taskHours;
  });

  const overtimeEmployees = Object.values(overtimeData).filter(hours => hours > 40).length;
  const totalOvertimeHours = Object.values(overtimeData).reduce((total, hours) => 
    total + Math.max(0, hours - 40), 0
  );

  // Calculate coverage gaps (hours with no staff)
  const hourlyTasks = {};
  for (let hour = 0; hour <= 23; hour++) {
    for (let day = 0; day < 7; day++) {
      const key = `${day}-${hour}`;
      hourlyTasks[key] = 0;
    }
  }

  currentWeekTasks.forEach(task => {
    const taskDate = new Date(task.shift_date);
    const dayOfWeek = taskDate.getDay();
    const startHour = parseInt(task.start_time.split(':')[0]);
    const endHour = parseInt(task.end_time.split(':')[0]);
    
    for (let hour = startHour; hour < endHour; hour++) {
      const key = `${dayOfWeek}-${hour}`;
      if (hourlyTasks[key] !== undefined) {
        hourlyTasks[key]++;
      }
    }
  });

  const uncoveredSlots = Object.values(hourlyTasks).filter(count => count === 0).length;
  const totalSlots = Object.keys(hourlyTasks).length;
  const coverageRate = ((totalSlots - uncoveredSlots) / totalSlots * 100);

  return (
    <Paper sx={{ 
      p: 2, 
      borderRadius: 2,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`,
      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TrendingUpIcon sx={{ color: 'primary.main', fontSize: 18 }} />
        <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem' }}>
          Week Overview
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.75rem' }}>
        {format(currentWeekStart, 'MMM d')} - {format(currentWeekEnd, 'MMM d, yyyy')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <StatCard
          icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
          title="Total Tasks"
          value={totalTasks}
          subtitle={`${totalTasks} tasks scheduled`}
          color={theme.palette.primary.main}
        />

        <StatCard
          icon={<GroupIcon sx={{ fontSize: 14 }} />}
          title="Employees With Tasks"
          value={`${employeesWithTasks}/${employees.length}`}
          subtitle={`${((employeesWithTasks / Math.max(employees.length, 1)) * 100).toFixed(0)}% utilization`}
          color={theme.palette.success.main}
        />

        {/* Additional stats */}
        {overtimeEmployees > 0 && (
          <Box sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
          }}>
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'error.main', fontSize: '0.7rem' }}>
              Overtime Alert
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', mt: 0.5 }}>
              {overtimeEmployees} employees over 40h ({totalOvertimeHours.toFixed(1)}h overtime)
            </Typography>
          </Box>
        )}

        <Box sx={{
          p: 1.5,
          borderRadius: 1,
          backgroundColor: alpha(theme.palette.info.main, 0.1),
          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
        }}>
          <Typography variant="caption" sx={{ fontWeight: 500, color: 'info.main', fontSize: '0.7rem' }}>
            Coverage Rate
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', mt: 0.5 }}>
            {coverageRate.toFixed(1)}% of time slots covered ({uncoveredSlots} gaps)
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default ScheduleStats;